import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Search } from "lucide-react";
import axios from "../../axios";
import apiError from "../../error";
import "./group-list-selection.css";

const getGroupUserCount = (group) => {
  const rawCount = [
    group.users,
    group.targets,
    group.userCount,
    group.usersCount,
    group.targetCount,
  ].find((value) => value !== undefined && value !== null && value !== "");
  const users = Number(rawCount ?? 0);

  return Number.isFinite(users) ? users : 0;
};

const normalizeGroups = (list) =>
  (Array.isArray(list) ? list : [])
    .filter((group) => group && (group.id !== undefined && group.id !== null))
    .map((group) => {
      return {
        id: group.id,
        name: group.name ?? group.groupName ?? "Untitled Group",
        users: getGroupUserCount(group),
      };
    })
    .filter((group) => group.users > 0);

const getGroupListKey = (groups) =>
  groups
    .map((group) => `${group.id}:${group.name}:${group.users}`)
    .join("|");

const GroupListSelection = ({ groupList, onDone, onCreateGroup }) => {
  const propGroups = useMemo(() => normalizeGroups(groupList), [groupList]);
  const propGroupsKey = useMemo(() => getGroupListKey(propGroups), [propGroups]);
  const lastAppliedPropGroupsKeyRef = useRef(null);
  const hasFetchedGroupsRef = useRef(false);

  const [groups, setGroups] = useState(propGroups);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/groups/parentGroup", {});

      const fetched = Array.isArray(res.data?.groups) ? res.data.groups : [];
      setGroups(normalizeGroups(fetched));
    } catch (err) {
      apiError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Use any provided groupList as a quick initial render without reacting to
  // identical arrays recreated by the chat renderer.
  useEffect(() => {
    if (lastAppliedPropGroupsKeyRef.current === propGroupsKey) return;

    lastAppliedPropGroupsKeyRef.current = propGroupsKey;
    setGroups(propGroups);
  }, [propGroups, propGroupsKey]);

  useEffect(() => {
    if (hasFetchedGroupsRef.current) return;

    hasFetchedGroupsRef.current = true;
    fetchGroups();
  }, [fetchGroups]);

  const trimmedSearch = search.trim().toLowerCase();

  const filteredGroups = useMemo(() => {
    if (!trimmedSearch) return groups;
    return groups.filter((group) =>
      String(group.name).toLowerCase().includes(trimmedSearch)
    );
  }, [groups, trimmedSearch]);

  const filteredIds = useMemo(() => filteredGroups.map((g) => g.id), [filteredGroups]);

  const allSelected =
    filteredGroups.length > 0 && filteredIds.every((id) => selected.includes(id));
  const someSelected =
    !allSelected && filteredIds.some((id) => selected.includes(id));

  const toggleAll = () => {
    setSelected((prev) => {
      if (allSelected) {
        return prev.filter((id) => !filteredIds.includes(id));
      }
      return Array.from(new Set([...prev, ...filteredIds]));
    });
  };

  const toggleRow = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleDone = () => {
    if (!onDone) return;
    const selectedGroups = groups.filter((group) => selected.includes(group.id));
    onDone(selected, selectedGroups);
  };

  return (
    <div className="gls-wrapper">
      <p className="gls-title">
        Let's set up your campaign. Which group would you like to target?
      </p>

      <div className="gls-search">
        <Search size={15} />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search groups"
          aria-label="Search groups"
        />
      </div>

      <div className="gls-container">
        <table className="gls-table">
          <thead>
            <tr>
              <th className="checkbox-col">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => el && (el.indeterminate = someSelected)}
                  onChange={toggleAll}
                  disabled={filteredGroups.length === 0}
                />
              </th>
              <th>Group Name</th>
              <th className="align-right">No. Of Users</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="gls-empty-row">
                  Loading groups...
                </td>
              </tr>
            ) : filteredGroups.length === 0 ? (
              <tr>
                <td colSpan={3} className="gls-empty-row">
                  {trimmedSearch ? "No matching groups found." : "No groups found."}
                </td>
              </tr>
            ) : (
              filteredGroups.map((group) => (
                <tr key={group.id} onClick={() => toggleRow(group.id)}>
                  <td className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={selected.includes(group.id)}
                      onChange={() => toggleRow(group.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>{group.name}</td>
                  <td className="align-right">{group.users}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="gls-footer">
        <div className="gls-footer-left">
          <button className="gls-done-btn" onClick={handleDone}>
            ✓ Done
          </button>
          <button className="gls-create-btn" onClick={onCreateGroup}>
            + Create Group
          </button>
        </div>
        <span className="gls-record-count">
          {filteredGroups.length} Records Found
        </span>
      </div>
    </div>
  );
};

export default GroupListSelection;
